import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  Box,
  Button,
} from "@mui/material";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, ReactNode, useCallback, useState } from "react";

import {
  CreateCampaignContactFieldMutation,
  CreateCampaignContactFieldMutationVariables,
  DeleteCampaignContactFieldMutation,
  DeleteCampaignContactFieldMutationVariables,
  EditCampaignContactFieldMappingMutation,
  EditCampaignContactFieldMappingMutationVariables,
  TrackedJobType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  createCampaignContactFieldUpdater,
  CREATE_CAMPAIGN_CONTACT_FIELD,
  deleteCampaignContactFieldUpdater,
  DELETE_CAMPAIGN_CONTACT_FIELD,
  editCampaignContactFieldMappingUpdater,
  EDIT_CAMPAIGN_CONTACT_FIELD_MAPPING,
} from "~frontend/graphql/campaigns";
import { palette } from "~frontend/theme";
import { scriptsContainField } from "~frontend/util/contactFields";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "../StepInstructions";
import { CampaignContactField, CampaignForEditor } from "../types";
import ContactFieldTableRow, {
  MappingCell,
  NameCell,
  ControlsCell,
} from "./ContactFieldTableRow";
import { SampleContactForm } from "./SampleContactForm";

function getDataSourceFieldLabel(campaign: CampaignForEditor): string {
  const loadJobType = campaign.contactImportJob?.type;

  switch (loadJobType) {
    case TrackedJobType.CampaignContactUpload:
      return "CSV Column";
    case TrackedJobType.CampaignContactActionKitImport:
      return "ActionKit Report Column";
    case TrackedJobType.CampaignContactGcsImport:
      return "CSV Column";
    case TrackedJobType.CampaignContactNgpvanImport:
      return "VAN Field";
    case TrackedJobType.CampaignContactActBlueImport:
      return "ActBlue Column";
    case TrackedJobType.CampaignContactFollowUpImport:
      return "Previous Campaign Field";
    default:
      return "CSV Column";
  }
}

export default function DataMapping({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const confirm = useConfirm();

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [addingField, setAddingField] = useState(false);
  const addDisabled = !!editingFieldId; // another field is being edited
  const dataSourceFieldLabel = getDataSourceFieldLabel(campaign);
  const contactDataKeys =
    campaign.contactImportJob?.result?.importedFieldNames ?? [];

  const [addContactField] = useMutation<
    CreateCampaignContactFieldMutation,
    CreateCampaignContactFieldMutationVariables
  >(CREATE_CAMPAIGN_CONTACT_FIELD, {
    update: createCampaignContactFieldUpdater,
    onErrorSnackbarMessage: "Error creating contact field",
  });

  const handleAddField = useCallback(
    async ({
      name,
      contactDataKey,
    }: {
      name: string;
      contactDataKey: string | null;
    }) => {
      const { errors } = await addContactField({
        variables: {
          input: {
            campaignId: campaign.id,
            name,
            contactDataKey,
          },
        },
      });
      if (errors) {
        throw errors;
      }
    },
    [addContactField, campaign]
  );

  const [editContactField] = useMutation<
    EditCampaignContactFieldMappingMutation,
    EditCampaignContactFieldMappingMutationVariables
  >(EDIT_CAMPAIGN_CONTACT_FIELD_MAPPING, {
    update: editCampaignContactFieldMappingUpdater,
    onErrorSnackbarMessage: "Error updating mapping",
  });
  const handleEditField = useCallback(
    async ({
      fieldId,
      contactDataKey,
      name,
    }: {
      fieldId: string;
      contactDataKey: string | null;
      name: string;
    }) => {
      const { errors } = await editContactField({
        variables: {
          input: {
            campaignId: campaign.id,
            fieldId,
            contactDataKey,
            name,
          },
        },
      });
      if (errors) {
        throw errors;
      }
    },
    [editContactField, campaign]
  );

  const [deleteContactField] = useMutation<
    DeleteCampaignContactFieldMutation,
    DeleteCampaignContactFieldMutationVariables
  >(DELETE_CAMPAIGN_CONTACT_FIELD, {
    update: deleteCampaignContactFieldUpdater,
    onErrorSnackbarMessage: "Error deleting contact field",
  });
  const handleDeleteField = useCallback(
    async (fieldId: string) => {
      const fieldName = campaign.contactFields.find(
        field => field.id === fieldId
      )!.name;
      const { confirmed } = await confirm({
        title: "Delete field",
        description: `Are you sure you want to delete "${fieldName}"?`,
      });
      if (!confirmed) {
        return;
      }

      const { errors } = await deleteContactField({
        variables: { input: { campaignId: campaign.id, fieldId } },
      });
      if (errors) {
        throw errors;
      }
    },
    [campaign, confirm, deleteContactField]
  );

  const renderContactField = (contactField: CampaignContactField) => {
    let required: false | { reason: string } = false;
    if (contactField.mappingRequired) {
      required = { reason: "this field is required for all campaigns" };
    } else if (scriptsContainField(campaign, contactField)) {
      required = { reason: "this field is used in a script" };
    }

    const editing = editingFieldId === contactField.id;
    const editDisabled = (!editing && !!editingFieldId) || addingField; // another field is being edited

    return (
      <ContactFieldTableRow
        key={contactField.id}
        contactField={contactField}
        onSubmit={({ contactDataKey, name }) =>
          handleEditField({ contactDataKey, name, fieldId: contactField.id })
        }
        onDelete={() => handleDeleteField(contactField.id)}
        contactDataKeys={contactDataKeys}
        required={required}
        campaign={campaign}
        dataSourceFieldLabel={dataSourceFieldLabel}
        editing={editing}
        editDisabled={editDisabled}
        onToggleEditing={editing =>
          setEditingFieldId(editing ? contactField.id : null)
        }
      />
    );
  };

  return (
    <FlexBox direction="column" gap={4}>
      <Box>
        <StepInstructions header="Map Your Contact Fields">
          Map the column names in your CSV to Contact Fields so that you're able
          to use them as variables in your initial message and canned responses.
        </StepInstructions>
      </Box>

      <Box sx={{ backgroundColor: palette.lightestGray, padding: 3 }}>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <NameCell sx={{ fontWeight: 600, color: palette.darkGray }}>
                CSV Column Name
              </NameCell>
              <MappingCell sx={{ fontWeight: 600, color: palette.darkGray }}>
                Map to Contact Field
              </MappingCell>
              <ControlsCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {_.sortBy(campaign.contactFields, "createdAt").map(
              renderContactField
            )}
            {addingField ? (
              <ContactFieldTableRow
                key="new"
                isNew
                onSubmit={({ contactDataKey, name }) =>
                  handleAddField({ contactDataKey, name })
                }
                contactDataKeys={contactDataKeys}
                required={false}
                campaign={campaign}
                dataSourceFieldLabel={dataSourceFieldLabel}
                editing={true}
                editDisabled={false}
                onToggleEditing={() => setAddingField(false)}
              />
            ) : (
              <TableRow>
                <ControlsCell
                  colSpan={3}
                  align="right"
                  style={{ borderBottom: 0 }}
                >
                  <Button
                    variant="contained"
                    color="accessory"
                    onClick={() => setAddingField(true)}
                    disabled={addDisabled}
                  >
                    + Add Contact Fields
                  </Button>
                </ControlsCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <SampleContactForm
        campaign={campaign}
        goToNextSection={goToNextSection}
      />
    </FlexBox>
  );
}

export function validateContactMapping({
  campaign,
}: {
  campaign: CampaignForEditor;
}): { title: string; message: ReactNode }[] {
  const requiredFieldsWithoutMappings = campaign.contactFields.filter(
    field =>
      (field.mappingRequired || scriptsContainField(campaign, field)) &&
      !field.contactDataKey
  );
  if (requiredFieldsWithoutMappings.length > 0) {
    return [
      {
        title: "Required Contact Fields Are Not Mapped",
        message: `Contact fields need to be mapped: ${requiredFieldsWithoutMappings
          .map(field => field.name)
          .join(", ")}`,
      },
    ];
  }

  return [];
}
